import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, SelectChangeEvent, Typography, Pagination } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { fetchOrdersSeller } from "../../api/orderFinalAPI";
import processingIcon from "../../assets/processing-icon.svg";
import readyToDispatchIcon from "../../assets/ready-to-dispatch.svg";
import dispatchedIcon from "../../assets/disptched.svg";
import deliveredIcon from "../../assets/delivered.svg";
import cancelIcon from "../../assets/cancel.svg";
import newIcon from "../../assets/new.svg";
import filterLines from "../../assets/filter-lines.svg";
import downloadCloud from "../../assets/download-cloud-02.svg";
import DataTable from "../../common/DataTable";
import { useNavigate } from "react-router-dom";
import { OrderFilterStatus, SellerOrder } from "../../types/Order";
import { getUserEmailFromLocalStorage } from "../../utils/localStorageUtils";
import SearchBar from "../../common/filters/SearchBar";
import { exportToExcel } from "../../utils/exportToExcel";
import { grandTotalAmountStyle } from "../customerManagement/DisplayCustomers.styles";

import {
  FiltersSection,
  FiltersRight,
  FilterSection,
  StyledSelect,
  FilterMenuItem,
  ExportButton,
  StyledCard,
  CustomerName,
  LocationText,
  OrderDetailsContainer,
  OrderIdText,
  OrderPriceText,
  PaymentStatusText,
  OrderStatusText,
  FilterExportText,
  TableLabelStyle,
  TableRowStyle,
  StyledMenuPaper,
} from "./OrderDetails.styles";
import useIsMobile from "../../common/mobileVersion/useIsMobile";
import { paginationMobVer } from "../ledger/ledgerStyles";
import {
  paginationCaptionStyle,
  paginationContainerStyle,
} from "../customerManagement/DisplayCustomers.styles";

const orderStatusOptions = [
  { value: "", label: "All", icon: filterLines },
  { value: "New", label: "New", icon: newIcon },
  { value: "Order Approved", label: "Order Approved", icon: processingIcon },
  { value: "Processing", label: "Processing", icon: processingIcon },
  {
    value: "Ready for Dispatch",
    label: "Ready for Dispatch",
    icon: readyToDispatchIcon,
  },
  { value: "Dispatched", label: "Dispatched", icon: dispatchedIcon },
  { value: "Delivered", label: "Delivered", icon: deliveredIcon },
  { value: "Cancelled", label: "Cancelled", icon: cancelIcon },
] as const;

const paymentStatusOptions = ["Complete", "Pending"] as const;

const OrderDetails = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const orgId = localStorage.getItem("orgId");
  const emailId = getUserEmailFromLocalStorage() || "";

  const [orders, setOrders] = useState<SellerOrder[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder] = useState("newest");
  const [orderStatusFilter, setOrderStatusFilter] = useState<
    OrderFilterStatus | ""
  >("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [allCustomers, setAllCustomers] = useState<
    { id: string; name: string }[]
  >([]);
  const [customerFilter, setCustomerFilter] = useState<string>("");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<
    "" | (typeof paymentStatusOptions)[number]
  >("");

  const [pagination, setPagination] = useState({
    page: 1,
    rowsPerPage: 10,
    totalRows: 0,
    totalPages: 1,
  });

  const columns = useMemo(
    () => [
      { label: "Customer", field: "customerName" },
      { label: "Date - Time", field: "date" },
      {
        label: "Location",
        field: "location",
        cellStyle: { width: "180px", maxWidth: "180px" },
      },
      { label: "Order Details", field: "orderDetails" },
      { label: "Payment", field: "paymentStatus" },
      {
        label: "Order Status",
        field: "orderStatus",
        cellStyle: { width: "15%" },
      },
    ],
    []
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 1500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  const { page, rowsPerPage, totalPages, totalRows } = pagination;
  const startIndex = (+page - 1) * +rowsPerPage;
  const endIndex = Math.min(startIndex + +rowsPerPage, +totalRows);

  useEffect(() => {
    const loadOrders = async () => {
      if (!emailId) return;
      try {
        const limit = isMobile ? 1000 : 10;
        const fetchedOrders = await fetchOrdersSeller({
          emailId,
          page,
          limit,
          search: debouncedSearchTerm,
          filter: orderStatusFilter || undefined,
          paymentStatus: paymentStatusFilter || undefined,
          customer: customerFilter || undefined,
        });

        setOrders(fetchedOrders.orders);
        if (allCustomers.length === 0) {
          const uniqueCustomers = Array.from(
            new Map(
              fetchedOrders.orders
                .filter((order) => order.customerId)
                .map((order) => [
                  order.customerId as string,
                  order.customerName,
                ])
            ).entries()
          ).map(([id, name]) => ({ id, name }));
          setAllCustomers(uniqueCustomers);
        }

        if (!isMobile) {
          const newPagination = {
            page: Number(fetchedOrders?.pagination?.currentPage) ?? 1,
            rowsPerPage: 10,
            totalRows: Number(fetchedOrders?.pagination?.totalRecords) ?? 1,
            totalPages: Number(fetchedOrders?.pagination?.totalPages) ?? 1,
          };
          setPagination((prev) => {
            const isSame =
              prev.page === newPagination.page &&
              prev.totalRows === newPagination.totalRows &&
              prev.totalPages === newPagination.totalPages;
            return isSame ? prev : newPagination;
          });
        }
      } catch (error) {
        console.error("Error fetching orders:", error);
      }
    };
    loadOrders();
  }, [
    sortOrder,
    orderStatusFilter,
    paymentStatusFilter,
    customerFilter,
    debouncedSearchTerm,
    page,
    emailId,
    isMobile,
  ]);

  const handleChangePage = (newPage: number) => {
    if (!isMobile) {
      setPagination((prev) => ({ ...prev, page: newPage }));
    }
  };

  const handleNavigation = useCallback(
    (id: string) => {
      navigate(`/maltora-seller/order-summary/${id}`);
    },
    [navigate, orgId]
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (e: SelectChangeEvent<unknown>) => {
    setOrderStatusFilter(e.target.value as OrderFilterStatus | "");
  };

  const handleCustomerFilterChange = (e: SelectChangeEvent<unknown>) => {
    setCustomerFilter(e.target.value as string);
  };

  const handlePaymentStatusFilterChange = (e: SelectChangeEvent<unknown>) => {
    setPaymentStatusFilter(
      e.target.value as "" | (typeof paymentStatusOptions)[number]
    );
  };

  const customerFilterOptions = allCustomers;

  const handleExport = async () => {
    try {
      const allOrdersResponse = await fetchOrdersSeller({
        emailId,
        page: 1,
        limit: pagination.totalRows,
        search: debouncedSearchTerm,
        filter: orderStatusFilter || undefined,
        paymentStatus: paymentStatusFilter || undefined,
        customer: customerFilter || undefined,
      });

      const allOrders = allOrdersResponse.orders;
      const excelData = allOrders.map((order) => ({
        Customer: order.customerName,
        Location: order.location,
        OrderID: order.orderDetails.orderId,
        Price: order.orderDetails.price,
        PaymentStatus: order.paymentStatus,
        OrderStatus: order.orderStatus,
        Outstanding: order.grandTotal,
      }));

      await exportToExcel(excelData, "orders_export.xlsx");
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  const orderTableData = useMemo(
    () =>
      orders.map((order) => {
        const numericGrandTotal = order.grandTotal
          ? parseFloat(order.grandTotal)
          : 0;
        const isPositive = numericGrandTotal >= 0;
        return {
          _fullOrderData: order,
          customerName: <CustomerName>{order.customerName}</CustomerName>,
          date: (() => {
            const d = new Date(order.orderedDate);
            const ist = new Date(d.getTime() + 5.5 * 60 * 60 * 1000);
            const day = ist.getUTCDate();
            const month = ist.toLocaleString("en-US", {
              month: "short",
              timeZone: "UTC",
            });
            const year = ist.getUTCFullYear();
            const rawHours = ist.getUTCHours();
            const ampm = rawHours >= 12 ? "PM" : "AM";
            const hours = String(rawHours % 12 || 12).padStart(2, "0");
            const minutes = String(ist.getUTCMinutes()).padStart(2, "0");
            return (
              <LocationText>{`${day} ${month} ${year}, ${hours}:${minutes} ${ampm}`}</LocationText>
            );
          })(),
          location: <LocationText>{order.location}</LocationText>,
          orderDetails: (
            <OrderDetailsContainer>
              <OrderIdText>{order.orderDetails.orderId}</OrderIdText>
              <OrderPriceText>
                {" "}
                {new Intl.NumberFormat("en-IN", {
                  style: "currency",
                  currency: "INR",
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }).format(order.orderDetails.price)}
              </OrderPriceText>
            </OrderDetailsContainer>
          ),
          paymentStatus: (
            <PaymentStatusText>{order.paymentStatus}</PaymentStatusText>
          ),
          orderStatus: <OrderStatusText>{order.orderStatus}</OrderStatusText>,
          outstanding: (
            <Typography
              sx={{ ...grandTotalAmountStyle(isPositive), fontWeight: "400" }}
            >
              {Math.abs(parseFloat(order.grandTotal ?? "0.00"))}
            </Typography>
          ),
        };
      }),
    [orders]
  );

  return (
    <>
      <FiltersSection>
        <SearchBar handleSearch={handleSearch} searchTerm={searchTerm} />

        <FiltersRight>
          <FilterSection sx={{ minWidth: "180px" }}>
            <StyledSelect
              value={customerFilter}
              onChange={handleCustomerFilterChange}
              displayEmpty
              IconComponent={KeyboardArrowDownIcon}
              renderValue={(selected) => {
                const selectedCustomer = customerFilterOptions.find(
                  (c) => c.id === selected
                );

                return (
                  <FilterExportText>
                    {selectedCustomer?.name || "Customer"}
                  </FilterExportText>
                );
              }}
              MenuProps={{
                PaperProps: {
                  sx: { ...StyledMenuPaper, minWidth: "220px" },
                },
              }}
            >
              <FilterMenuItem value="">All</FilterMenuItem>
              {customerFilterOptions.map((customer) => (
                <FilterMenuItem key={customer.id} value={customer.id}>
                  {customer.name}
                </FilterMenuItem>
              ))}
            </StyledSelect>
          </FilterSection>

          <FilterSection sx={{ minWidth: "160px" }}>
            <StyledSelect
              value={paymentStatusFilter}
              onChange={handlePaymentStatusFilterChange}
              displayEmpty
              IconComponent={KeyboardArrowDownIcon}
              renderValue={(selected) => {
                const selectedValue = selected as string;

                return (
                  <FilterExportText>
                    {selectedValue || "Payment Status"}
                  </FilterExportText>
                );
              }}
              MenuProps={{
                PaperProps: {
                  sx: StyledMenuPaper,
                },
              }}
            >
              <FilterMenuItem value="">All</FilterMenuItem>
              {paymentStatusOptions.map((option) => (
                <FilterMenuItem key={option} value={option}>
                  {option}
                </FilterMenuItem>
              ))}
            </StyledSelect>
          </FilterSection>

          <FilterSection>
            <StyledSelect
              value={orderStatusFilter}
              onChange={handleFilterChange}
              displayEmpty
              IconComponent={KeyboardArrowDownIcon}
              renderValue={(selected) => {
                const selectedValue = selected as OrderFilterStatus | "";
                const selectedOption = orderStatusOptions.find(
                  (option) => option.value === selectedValue
                );
                const labelToDisplay = !selectedValue
                  ? "Order Status"
                  : selectedOption?.label || "Order Status";

                return <FilterExportText>{labelToDisplay}</FilterExportText>;
              }}
              MenuProps={{
                PaperProps: {
                  sx: StyledMenuPaper,
                },
              }}
            >
              {orderStatusOptions.map((option) => (
                <FilterMenuItem key={option.value} value={option.value}>
                  <img src={option.icon} alt={option.value} />
                  {option.label}
                </FilterMenuItem>
              ))}
            </StyledSelect>
          </FilterSection>

          <ExportButton onClick={handleExport}>
            <img
              src={downloadCloud}
              alt="export"
              width={19.23}
              height={19.23}
            />
            <FilterExportText>Export</FilterExportText>
          </ExportButton>
        </FiltersRight>
      </FiltersSection>

      <StyledCard>
        <DataTable
          columns={columns.map((col) => ({
            ...col,
            align: "left",
            labelStyle: {
              TableLabelStyle,
            },
          }))}
          data={orderTableData}
          emptyStateMessage="No Orders Available"
          rowStyle={() => TableRowStyle}
          onRowClick={(rowData) => {
            handleNavigation(rowData._fullOrderData.id);
          }}
        />
      </StyledCard>
      <Box>
        {!isMobile && (
          <Box sx={paginationContainerStyle}>
            <Typography variant="subtitle2" sx={paginationCaptionStyle}>
              Showing data {startIndex + 1} to {endIndex} of {totalRows} entries
            </Typography>
            <Pagination
              count={totalPages}
              page={+page}
              onChange={(event, value) => handleChangePage(value)}
              shape="rounded"
              variant="outlined"
              sx={paginationMobVer(isMobile)}
            />
          </Box>
        )}
      </Box>
    </>
  );
};

export default OrderDetails;
